import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";

const AdminPromptConfig = () => {
    const [prompt, setPrompt] = useState("");
    const [updatedAt, setUpdatedAt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/prompt-config");
                setPrompt(data.system_prompt || "");
                setUpdatedAt(data.updated_at);
            } catch {
                toast.error("Failed to load prompt");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/admin/prompt-config", { system_prompt: prompt });
            toast.success("Prompt updated");
            setUpdatedAt(new Date().toISOString());
        } catch {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="prompt-loading">
                <Loader2 className="w-6 h-6 animate-spin text-un-berry" />
            </div>
        );
    }

    return (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="admin-prompt">
            <div className="un-label text-un-grape mb-3">Admin · Prompt config</div>
            <h1 className="un-h1 text-5xl md:text-6xl mb-4">
                Master <span className="text-un-berry">Strategist</span> Prompt.
            </h1>
            <p className="text-un-onyx/70 max-w-2xl mb-8">
                This is the system instructions applied to every script generation. Edits take effect immediately for new scripts.
            </p>
            {updatedAt && (
                <div className="un-mono text-xs text-un-onyx/50 mb-4">
                    Last updated {new Date(updatedAt).toLocaleString()}
                </div>
            )}

            <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={28}
                className="un-input un-mono text-sm leading-relaxed"
                data-testid="prompt-textarea"
                spellCheck={false}
            />

            <div className="flex items-center justify-between gap-3 mt-6">
                <div className="un-mono text-xs text-un-onyx/50">
                    {prompt.length.toLocaleString()} characters
                </div>
                <button
                    onClick={save}
                    disabled={saving}
                    className="un-btn-primary flex items-center gap-2"
                    data-testid="prompt-save-button"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {saving ? "Saving…" : "Save prompt"}
                </button>
            </div>
        </div>
    );
};

export default AdminPromptConfig;
